// FIND (web-ui-issues #26): pure helpers for in-transcript search — case-insensitive matching
// over segment text, plus safe (never dangerouslySetInnerHTML) highlight-range slicing, in the
// same "split into typed pieces, let the caller map those to elements" spirit as SearchSnippet /
// parseSnippetSegments in components/ui.tsx.
//
// Composition-with-word-spans choice (documented per the FIND task): matches are always found
// and numbered against a segment's canonical `text` — the same string wordAlignment.ts aligns
// against (REPLAY-2's invariant: a segment's aligned words, joined with a single space,
// reproduce `segment.text` exactly). Rather than re-running the search independently per
// rendering unit — which would silently break a multi-word/phrase query that straddles a word
// boundary if each word span matched on its own text alone — a match's [start, end) character
// range is computed once against the canonical text and then *reprojected* onto whichever unit
// is actually rendering: the whole segment text in the plain-text fallback (offset 0), or each
// word's own slice of that same text where word spans render (offset = that word's start
// position, itself derived from the same join(' ') invariant). `sliceHighlight` below is that
// single reprojection primitive — one implementation, correct in both places, rather than two
// "simplest per unit" ones that would disagree on phrase queries.
import { Fragment, type ReactNode } from 'react';

export interface MatchRange {
  start: number;
  end: number;
}

/** A match range plus its position (0-based) in the whole-transcript, segment-order match
 * list — this is what lets "current match" emphasis, the "N of M" count, and scroll-to-current
 * all agree on which single match is active. */
export interface IndexedMatch extends MatchRange {
  globalIndex: number;
}

interface HighlightPiece {
  text: string;
  match: boolean;
  current: boolean;
}

/** Escapes regex metacharacters so arbitrary user input is always treated as a literal
 * substring, never as a regex — a query like `"a.b (c)"` or `"c++"` matches only that literal
 * text instead of throwing or matching unrelated text as a wildcard. */
export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive, non-overlapping match ranges for `query` within `text`, in left-to-right
 * order. Returns `[]` for an empty/whitespace-only query — "empty query = no highlights". */
export function findMatches(text: string, query: string): MatchRange[] {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const re = new RegExp(escapeRegExp(trimmed), 'gi');
  const matches: MatchRange[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    matches.push({ start: m.index, end: m.index + m[0].length });
    // `m[0]` is always non-empty here (query is a non-empty literal pattern), so
    // `re.lastIndex` always advances on its own — no zero-width-match infinite-loop guard
    // is needed the way a `*`/`?`-containing pattern would require.
  }
  return matches;
}

/** Projects canonical-text match ranges onto one rendering unit's own slice of that text
 * (`offset` = where that slice starts within the canonical text), splitting it into
 * plain/match/current-match pieces ready to render as text nodes and `<mark>`s. See the file
 * header for why this same function serves both the whole-segment and per-word cases.
 * `currentGlobalIndex` of `-1` means no match is current. */
export function sliceHighlight(
  text: string,
  offset: number,
  matches: IndexedMatch[],
  currentGlobalIndex: number,
): HighlightPiece[] {
  const len = text.length;
  const local = matches
    .map((m) => ({ start: m.start - offset, end: m.end - offset, globalIndex: m.globalIndex }))
    .filter((m) => m.end > 0 && m.start < len)
    .map((m) => ({
      start: Math.max(0, m.start),
      end: Math.min(len, m.end),
      globalIndex: m.globalIndex,
    }))
    .sort((a, b) => a.start - b.start);

  const pieces: HighlightPiece[] = [];
  let pos = 0;
  for (const m of local) {
    if (m.end <= pos) continue; // fully-consumed overlap (shouldn't happen for non-overlapping matches, but stay safe)
    const start = Math.max(m.start, pos);
    if (start > pos) pieces.push({ text: text.slice(pos, start), match: false, current: false });
    pieces.push({ text: text.slice(start, m.end), match: true, current: m.globalIndex === currentGlobalIndex });
    pos = m.end;
  }
  if (pos < len) pieces.push({ text: text.slice(pos), match: false, current: false });
  if (pieces.length === 0) pieces.push({ text, match: false, current: false });
  return pieces;
}

/** Renders `text` with any matches (in canonical-text coordinates, `offset`-adjusted) wrapped
 * in real `<mark>` elements — never `dangerouslySetInnerHTML`. Every non-matching character,
 * including one that happens to look like markup, renders as an inert text node exactly like
 * any other React string child (same safety property `SearchSnippet` documents in ui.tsx).
 * Returns `text` unchanged (no wrapper elements at all) when there's nothing to highlight, so
 * an empty query costs nothing beyond the `matches.length === 0` check. */
export function renderHighlighted(
  text: string,
  offset: number,
  matches: IndexedMatch[] | undefined,
  currentGlobalIndex: number,
): ReactNode {
  if (!matches || matches.length === 0) return text;
  return sliceHighlight(text, offset, matches, currentGlobalIndex).map((piece, i) =>
    piece.match ? (
      <mark key={i} className={'find-match' + (piece.current ? ' find-match-current' : '')}>
        {piece.text}
      </mark>
    ) : (
      <Fragment key={i}>{piece.text}</Fragment>
    ),
  );
}
